import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { ORDER_INSTRUCTION_MAX_LENGTH, type Order } from '@klareospace/pharos-lib-types';
import { Textarea } from '@klareospace/pharos-lib-web-utils';

import { formatInstruction } from '../utils';
import { useInlineOrderFieldEdit } from '../hooks/use-inline-order-field-edit';
import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { InlineEditActions } from './inline-edit-actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InstructionInlineEditorProps {
  readonly fieldState: OrderFieldEditState;
  readonly lockReason: OrderFieldLockReason | null;
  readonly order: Order;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isValidInstruction(value: string): boolean {
  return value.length <= ORDER_INSTRUCTION_MAX_LENGTH;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InstructionInlineEditor({
  fieldState,
  lockReason,
  order,
}: InstructionInlineEditorProps): React.JSX.Element {
  const { t } = useTranslation('orders');

  const { draft, handleCancel, handleConfirm, isEditing, isPending, isValid, setDraft, startEdit } =
    useInlineOrderFieldEdit<string>({
      // A blank draft that started blank (order never had an instruction) sends no patch at
      // all — confirming without typing anything must not turn an absent instruction into a
      // stored empty string. A blank draft that started non-blank is a deliberate clear and
      // must still send an explicit `''` (backend distinguishes "clear" from "leave untouched"
      // by `undefined` vs `''`, same as the KLAREO connector's update-path contract).
      buildPatch: (value) => {
        const trimmed = value.trim();
        return trimmed || order.instruction ? { instruction: trimmed } : {};
      },
      isValid: isValidInstruction,
      order,
      successKey: 'drawer.edit.content.success',
      value: order.instruction ?? '',
    });

  if (isEditing) {
    return (
      <div className="flex flex-col gap-1.5">
        <Textarea
          aria-label={t('drawer.edit.content.instructionAriaLabel')}
          className="min-h-[100px] text-sm"
          disabled={isPending}
          maxLength={ORDER_INSTRUCTION_MAX_LENGTH}
          onChange={(e): void => setDraft(e.target.value)}
          value={draft}
        />
        <div className="flex items-center justify-end gap-1">
          <InlineEditActions
            isPending={isPending}
            isValid={isValid}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
          />
        </div>
      </div>
    );
  }

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={startEdit}>
      <span className="text-sm text-foreground whitespace-pre-wrap break-words">
        {formatInstruction(order.instruction)}
      </span>
    </EditableFieldWrapper>
  );
}
